/**
 * Segmented control — small tablist for filter/toggle strips.
 *
 * Neither `Tabs` (page-scale, mobile modal, animated content panels)
 * nor `ButtonGroup` (icon-only, no active state) fits an inline
 * segmented filter. This is the shared primitive for compact filter
 * chip strips (comments gutter Open/Resolved/All, etc.).
 *
 * Renders as `role="tablist"` with each option as a `role="tab"`
 * button. Uses theme colours so the active state matches the app
 * primary. Keeps every button small enough to sit inside a gutter
 * header row.
 */

import { ReactNode, useContext } from 'react';
import cn from 'classnames';
import { ThemeContext } from '../../../utils/providers/theme-context';

export interface SegmentedControlOption<V extends string> {
  value: V;
  label: ReactNode;
  /** Optional count rendered in muted parens after the label. */
  count?: number;
  disabled?: boolean;
}

export interface SegmentedControlProps<V extends string> {
  options: SegmentedControlOption<V>[];
  value: V;
  onChange: (next: V) => void;
  ariaLabel: string;
  size?: 'sm' | 'md';
  className?: string;
}

export function SegmentedControl<V extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  size = 'sm',
  className
}: SegmentedControlProps<V>) {
  const themeCtx = useContext(ThemeContext);
  const getColour = themeCtx?.getColour;
  const border = getColour?.('border') ?? 'inherit';

  // Explicit height per size (instead of padY) so segments stay the
  // same height whether or not an option carries a `(count)` suffix,
  // and so the control lines up with neighbouring header buttons.
  const sizes = {
    sm: {
      text: 'text-[10px]',
      height: 'h-7',
      padX: 'px-2'
    },
    md: {
      text: 'text-xs',
      height: 'h-8',
      padX: 'px-3'
    }
  }[size];

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex rounded-full border overflow-hidden shrink-0 max-w-max',
        className
      )}
      style={{ borderColor: border }}
    >
      {options.map(opt => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            role="tab"
            type="button"
            aria-selected={active}
            disabled={opt.disabled}
            onClick={() => onChange(opt.value)}
            className={cn(
              'inline-flex items-center justify-center uppercase tracking-wider font-semibold transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              'disabled:opacity-40 disabled:cursor-not-allowed',
              sizes.text,
              sizes.padX,
              sizes.height
            )}
            style={{
              backgroundColor: active
                ? getColour?.('activeText')
                : getColour?.('secondLayer'),
              color: active
                ? getColour?.('firstLayer')
                : getColour?.('normalText')
            }}
          >
            {opt.label}
            {typeof opt.count === 'number' && opt.count > 0 && (
              <span className="ml-1 opacity-70">({opt.count})</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
